import { useEffect, useRef, type RefObject } from "react";

export interface LoadMoreOptions {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => unknown;
  /** Scrolling element containing the sentinel; the viewport when omitted. */
  rootRef?: RefObject<HTMLElement | null>;
}

/**
 * Infinite scroll: returns a ref for a sentinel element placed after the last
 * item; the next page loads when the sentinel scrolls into view.
 */
export const useLoadMoreOnScroll = ({
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  rootRef,
}: LoadMoreOptions) => {
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage || isFetchingNextPage) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) fetchNextPage();
      },
      // Start loading a little before the very end
      { root: rootRef?.current ?? null, rootMargin: "0px 0px 200px 0px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, rootRef]);

  return sentinelRef;
};
